import React, { useState, useMemo, useRef, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';
import Pagination from '../../Common/DaisyTable/Pagination/Pagination';
import { formatDate } from '../../../utils/format.utils';
import LoadingSpinner from '../../Common/Loading/LoadingSpinner';
import MixtureDetails from './MixtureDetails';
import sortingUtils from '../../../utils/sorting.utils';
import { filterFns, flexRender, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import ReagentsModal from '../../Capsules/ReagentsModal';

// Composant Arrow pour les indicateurs de tri
const SortArrow = React.memo(({ direction }) => {
    if (!direction) return null;

    return direction === 'asc' ? (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
        </svg>
    ) : (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
});

// Composant pour les en-têtes de colonnes
const TableHeader = React.memo(({ header }) => {
    const direction = header.column.getIsSorted();

    return (
        <th key={header.id} colSpan={header.colSpan} style={{ width: header.getSize() }} className='relative'>
            {header.isPlaceholder ? null : (
                <div
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer flex gap-4 items-center"
                >
                    {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                    )}
                    <SortArrow direction={direction} />
                </div>
            )}
            <div
                onDoubleClick={() => header.column.resetSize()}
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
            />
        </th>
    );
});

// Composant pour une ligne de tableau
const TableRow = React.memo(({ row, onRowClick }) => {
    return (
        <tr className="hover:bg-base-200 cursor-pointer" onClick={(e) => {
            e.stopPropagation()
            onRowClick(row.original)
        }}>
            {row.getVisibleCells().map((cell) => (
                <td key={cell.id} style={{ width: cell.column.getSize() }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
            ))}
        </tr>
    );
});



const fetchMixtures = async () => {
    const response = await api.get(API.ADMIN_ALL_MIXTURES);
    if (response.status === 200) {
        return Array.isArray(response.data) ? response.data : [];
    }
    return [];
};

const MixturesManagement = () => {

    const columns = [
        {
            id: 'name',
            accessorFn: row => row.name,
            header: () => <span>Nom</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            filterFn: filterFns.includesString,
            sortingFn: sortingUtils.sortText,
            size: 200,
        },
        {
            id: 'type',
            accessorFn: row => row.type,
            header: () => <span>Type</span>,
            cell: ({ row }) => (
                <div className="badge badge-outline">
                    {row.original.private ? 'Personnalisé' : 'Catalogue'}
                </div>
            ),
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
        },
        {
            id: 'category',
            accessorFn: row => row.category,
            header: () => <span>Cat. Principale</span>,
            cell: info => (
                <span >
                    {info.getValue()}
                </span>
            ),
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
        },
        {
            id: 'subCategory',
            accessorFn: row => row.subCategory,
            header: () => <span>Cat. Secondaire</span>,
            cell: info => (
                <span >
                    {info.getValue()}
                </span>
            ),
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
        },
        {
            id: 'defaultDilution.label',
            accessorFn: row => row.defaultDilution?.label,
            header: () => <span>Dilution</span>,
            cell: info => <span>{info.getValue()}</span>,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            size: 50,
        },
        {
            id: 'ref',
            accessorFn: row => row.ref,
            header: () => <span>Référence</span>,
            cell: info => <span>{info.getValue()}</span>,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            size: 50,

        },
        {
            id: 'reagents',
            accessorFn: row => row.reagents,
            header: () => <span>Réactifs</span>,
            cell: (info) => <ReagentsModal mixtureName={info?.row?.original?.name} reagents={info.getValue()} description={info?.row?.original?.description} />,
            filterFn: filterFns.arrIncludes,
            enableSorting: false,
        },
        {
            id: 'creationDate',
            accessorFn: row => row.creationDate,
            header: () => <span>Date de création</span>,
            cell: info => formatDate(info.getValue(), false),
            sortingFn: sortingUtils.sortDate,
            filterFn: filterFns.includesString,
        },
        {
            id: 'actions',
            accessorFn: row => row.id,
            header: () => <span>Actions</span>,
            cell: ({ row }) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        console.log(row.original)
                        handleOpenDetails(row.original);
                    }}
                >
                    Détails
                </button>
            ),
            enableSorting: false,
            enableGlobalFilter: false,
        },
    ];

    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedMixture, setSelectedMixture] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCatalogue, setSelectedCatalogue] = useState('physiques');
    const [showCustomOnly, setShowCustomOnly] = useState(false);
    const [showCatalogueOnly, setShowCatalogueOnly] = useState(false);
    const [selectedSubCategory, setSelectedSubCategory] = useState('');

    // Pagination state
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    // Query for fetching all mixtures
    const {
        data,
        error,
        isLoading,
        isError,
        refetch,
        isFetching
    } = useQuery({
        queryKey: ['mixtures'],
        queryFn: fetchMixtures,
        select: (data) => {
            return Array.isArray(data) ? data : [];
        }
    });

    // Filter mixtures based on search query and selected category
    const filteredData = useMemo(() => {
        if (!Array.isArray(data)) return [];

        return data.filter(mixture => {
            const matchesSearch = mixture.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                mixture.description?.toLowerCase().includes(searchQuery.toLowerCase());

            // Filtre par catalogue
            const matchesCatalogue = selectedCatalogue === 'tous' ||
                (selectedCatalogue === 'physiques' && mixture.category === 'Physique') ||
                (selectedCatalogue === 'effluences' && mixture.category === 'Effluences') ||
                (selectedCatalogue === 'autres' && mixture.category === 'Autres');

            // Filtre par type (personnalisé ou catalogue)
            const matchesType = showCustomOnly ? mixture.private :
                showCatalogueOnly ? !mixture.private : true;

            // Filtre par catégorie secondaire
            const matchesSubCategory = !selectedSubCategory || mixture.subCategory === selectedSubCategory;

            return matchesSearch && matchesCatalogue && matchesType && matchesSubCategory;
        });
    }, [data, searchQuery, selectedCatalogue, showCustomOnly, showCatalogueOnly, selectedSubCategory]);

    // Calculate the current page data
    const currentPageData = useMemo(() => {
        if (!filteredData) return [];
        const start = pagination.pageIndex * pagination.pageSize;
        const end = start + pagination.pageSize;
        return filteredData.slice(start, end);
    }, [filteredData, pagination]);

    // Calculate total pages
    const pageCount = useMemo(() => {
        if (!filteredData) return 0;
        return Math.ceil(filteredData.length / pagination.pageSize);
    }, [filteredData, pagination.pageSize]);

    // Create a mock table object for the Pagination component
    const mockTable = useMemo(() => ({
        getPageCount: () => pageCount,
        setPageIndex: (index) => {
            setPagination(prev => ({ ...prev, pageIndex: index }));
        },
        setPageSize: (size) => setPagination(prev => ({ ...prev, pageSize: size, pageIndex: 0 })),
        nextPage: () => setPagination(prev => ({ ...prev, pageIndex: Math.min(prev.pageIndex + 1, pageCount - 1) })),
        previousPage: () => setPagination(prev => ({ ...prev, pageIndex: Math.max(prev.pageIndex - 1, 0) })),
        getRowCount: () => filteredData?.length || 0
    }), [pageCount, filteredData]);

    const handleFetchAll = () => {
        console.log("All data is already fetched");
    };

    const handleOpenDetails = (mixture = null) => {
        setSelectedMixture(mixture);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetails = () => {
        setIsDetailsModalOpen(false);
        setSelectedMixture(null);
    };


    const tableRef = useRef(null);

    // Effect to scroll to top when pagination changes
    useEffect(() => {
        if (tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [pagination.pageIndex]);

    if (isError) {
        return <div className="alert alert-error">Une erreur est survenue : {error.message}</div>;
    }



    const table = useReactTable({
        columns,
        data: currentPageData,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });



    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Gestion des Mélanges</h2>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-outline"
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        {isFetching ? 'Actualisation...' : 'Actualiser'}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => handleOpenDetails()}
                    >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Ajouter un mélange
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                {/* Barre de recherche et filtres */}
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Rechercher un mélange..."
                            className="input input-bordered w-full"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    
                    {/* Menu des filtres */}
                    <div className="dropdown dropdown-end">
                        <div tabIndex={0} role="button" className="btn btn-outline">
                            Filtres
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-1">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
                            </svg>
                        </div>
                        <ul tabIndex={0} className="dropdown-content z-[1] menu p-4 shadow bg-base-100 rounded-box w-96">
                            {/* Catalogue */}
                            <li className="menu-title">
                                <span>Catalogue</span>
                            </li>
                            <li>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        className={`btn btn-sm ${selectedCatalogue === 'tous' ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => setSelectedCatalogue('tous')}
                                    >
                                        Tous
                                    </button>
                                    <button
                                        className={`btn btn-sm ${selectedCatalogue === 'physiques' ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => setSelectedCatalogue('physiques')}
                                    >
                                        Physiques
                                    </button>
                                    <button
                                        className={`btn btn-sm ${selectedCatalogue === 'effluences' ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => setSelectedCatalogue('effluences')}
                                    >
                                        Effluences
                                    </button>
                                    <button
                                        className={`btn btn-sm ${selectedCatalogue === 'autres' ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => setSelectedCatalogue('autres')}
                                    >
                                        Autres
                                    </button>
                                </div>
                            </li>

                            {/* Type de mélanges */}
                            <li className="menu-title mt-4">
                                <span>Type de mélanges</span>
                            </li>
                            <li>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        className={`btn btn-sm ${!showCustomOnly && !showCatalogueOnly ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => {
                                            setShowCustomOnly(false);
                                            setShowCatalogueOnly(false);
                                        }}
                                    >
                                        Tous
                                    </button>
                                    <button
                                        className={`btn btn-sm ${showCatalogueOnly ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => {
                                            setShowCustomOnly(false);
                                            setShowCatalogueOnly(true);
                                        }}
                                    >
                                        Catalogue
                                    </button>
                                    <button
                                        className={`btn btn-sm ${showCustomOnly ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => {
                                            setShowCustomOnly(true);
                                            setShowCatalogueOnly(false);
                                        }}
                                    >
                                        Personnalisés
                                    </button>
                                </div>
                            </li>

                            {/* Catégorie secondaire */}
                            <li className="menu-title mt-4">
                                <span>Catégorie secondaire</span>
                            </li>
                            <li>
                                <select 
                                    className="select select-primary select-bordered select-md w-full"
                                    value={selectedSubCategory}
                                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                                >
                                    <option value="">Toutes les catégories</option>
                                    {[...new Set(data?.filter(item => {
                                        if (selectedCatalogue === 'tous') return true;
                                        if (selectedCatalogue === 'physiques') return item.category === 'Physique';
                                        if (selectedCatalogue === 'effluences') return item.category === 'Effluences';
                                        if (selectedCatalogue === 'autres') return item.category === 'Autres';
                                        return false;
                                    }).map(item => item.subCategory))].map(subCategory => (
                                        <option key={subCategory} value={subCategory}>
                                            {subCategory}
                                        </option>
                                    ))}
                                </select>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Badges des filtres actifs */}
                <div className="flex flex-wrap gap-2">
                    {selectedCatalogue !== 'tous' && (
                        <div className="badge badge-primary gap-1">
                            {selectedCatalogue === 'physiques' ? 'Physiques' :
                             selectedCatalogue === 'effluences' ? 'Effluences' :
                             'Autres'}
                            <button className="btn btn-xs btn-ghost" onClick={() => setSelectedCatalogue('tous')}>×</button>
                        </div>
                    )}
                    {(showCustomOnly || showCatalogueOnly) && (
                        <div className="badge badge-primary gap-1">
                            {showCustomOnly ? 'Personnalisés' : 'Catalogue'}
                            <button className="btn btn-xs btn-ghost" onClick={() => {
                                setShowCustomOnly(false);
                                setShowCatalogueOnly(false);
                            }}>×</button>
                        </div>
                    )}
                    {selectedSubCategory && (
                        <div className="badge badge-primary gap-1">
                            {selectedSubCategory}
                            <button className="btn btn-xs btn-ghost" onClick={() => setSelectedSubCategory('')}>×</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4" ref={tableRef}>
                <div className="overflow-hidden">
                    <table  className="table table-sm overflow-x-auto m-auto border-spacing-0">
                        <thead>
                            <tr>
                                {table.getFlatHeaders().map((header) => (
                                    <TableHeader key={header.id} header={header} />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    row={row}
                                    onRowClick={handleOpenDetails}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 flex justify-end">
                    <Pagination
                        server={{
                            total: filteredData?.length || 0,
                            fetched: filteredData?.length || 0,
                        }}
                        client={pagination}
                        table={mockTable}
                        handleFetchAll={handleFetchAll}
                    />
                </div>
            </div>

            <MixtureDetails
                isOpen={isDetailsModalOpen}
                onClose={handleCloseDetails}
                mixture={selectedMixture}
            />
        </div>
    );
};

export default MixturesManagement; 